import { useEffect, useId, useRef, useState } from 'react'
import type { NoteExportController, NoteExportJob } from '../exports/client'
import type { NoteExportOptions } from '../exports/contract'
import { Icon } from './Icon'
import { ProcessingIndicator } from './ProcessingIndicator'
import { notify } from './Notifications'

const ACTIVE_STATES = new Set(['queued', 'waiting_for_images', 'preparing', 'finalizing', 'canceling'])
const exportDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

export function NoteExportDialog({
  imageCount,
  noteCount,
  onClose,
  onQueue,
}: {
  imageCount: number
  noteCount: number
  onClose(): void
  onQueue(options: NoteExportOptions): void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const [format, setFormat] = useState<NoteExportOptions['format']>('markdown')
  const [includeImages, setIncludeImages] = useState(true)
  const [includeTags, setIncludeTags] = useState(true)
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    const previousFocus = document.activeElement
    dialog.showModal()
    return () => {
      dialog.close()
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus({ preventScroll: true })
      }
    }
  }, [])
  return (
    <dialog
      ref={dialogRef}
      className="modal note-selection-dialog note-export-dialog"
      aria-labelledby={titleId}
      onCancel={(event) => { event.preventDefault(); onClose() }}
    >
      <form
        className="modal-box note-selection-dialog__panel note-export-dialog__panel"
        onSubmit={(event) => {
          event.preventDefault()
          onQueue({ format, includeImages, includeTags })
        }}
      >
        <header className="note-export-dialog__header">
          <div>
            <h2 id={titleId}>Export {noteCount} note{noteCount === 1 ? '' : 's'}</h2>
            <p>The ZIP will be prepared in the background and kept for 30 days.</p>
          </div>
          <button className="btn btn-ghost" type="button" onClick={onClose} aria-label="Close export options"><Icon name="close" /></button>
        </header>
        <div className="note-selection-dialog__scroll">
          <fieldset className="note-export-options">
            <legend>Format</legend>
            <label className="note-export-option">
              <input className="radio radio-primary" type="radio" name="note-export-format" checked={format === 'markdown'} onChange={() => setFormat('markdown')} />
              <span><strong>Markdown</strong><small>One readable file per note, with relative image links.</small></span>
            </label>
            <label className="note-export-option">
              <input className="radio radio-primary" type="radio" name="note-export-format" checked={format === 'json'} onChange={() => setFormat('json')} />
              <span><strong>JSON</strong><small>One documented notes.json file for transfer or processing.</small></span>
            </label>
          </fieldset>
          <fieldset className="note-export-preferences">
            <legend>Include</legend>
            <label className="note-export-preference">
              <span><strong>Images</strong><small>{imageCount} stored attachment{imageCount === 1 ? '' : 's'}; URL-preview images are excluded.</small></span>
              <input className="toggle toggle-primary" type="checkbox" checked={includeImages} onChange={(event) => setIncludeImages(event.target.checked)} />
            </label>
            <label className="note-export-preference">
              <span><strong>Tags</strong><small>User-applied tags only.</small></span>
              <input className="toggle toggle-primary" type="checkbox" checked={includeTags} onChange={(event) => setIncludeTags(event.target.checked)} />
            </label>
          </fieldset>
        </div>
        <div className="modal-action">
          <button className="btn btn-ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" type="submit">Prepare export</button>
        </div>
      </form>
    </dialog>
  )
}

function stateLabel(job: NoteExportJob): string {
  if (job.state === 'waiting_for_images') return 'Waiting for images to sync'
  if (job.state === 'queued') return 'Queued'
  if (job.state === 'preparing') return job.phase === 'adding-images' ? 'Adding images' : 'Preparing notes'
  if (job.state === 'finalizing') return 'Finalizing ZIP'
  if (job.state === 'ready') return 'Ready to download'
  if (job.state === 'failed') return 'Export needs attention'
  if (job.state === 'canceling') return 'Canceling'
  if (job.state === 'canceled') return 'Canceled'
  return 'Expired'
}

function jobPercent(job: NoteExportJob): number {
  if (job.state === 'ready') return 100
  const total = job.note_count + job.total_images
  return total > 0 ? Math.min(99, Math.round(((job.completed_notes + job.completed_images) / total) * 100)) : 0
}

function bytes(value: number | null): string {
  if (value === null) return ''
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`
  return `${(value / (1024 * 1024)).toFixed(value >= 10 * 1024 * 1024 ? 0 : 1)} MB`
}

function expiryLabel(job: NoteExportJob): string {
  if (job.state === 'expired') return 'Expired and no longer downloadable'
  if (job.state === 'canceled') return 'Canceled; temporary files removed'
  if (job.state === 'failed') return 'Failed; temporary files expire automatically'
  const days = Math.max(0, Math.ceil((Date.parse(job.expires_at) - Date.now()) / 86_400_000))
  return `Available for ${days} more day${days === 1 ? '' : 's'}`
}

function requestedAtLabel(job: NoteExportJob): string {
  const requestedAt = new Date(job.created_at)
  return Number.isNaN(requestedAt.getTime()) ? 'Date unavailable' : exportDateTimeFormatter.format(requestedAt)
}

function ExportJobAction({
  compact = false,
  controller,
  job,
}: {
  compact?: boolean
  controller: NoteExportController
  job: NoteExportJob
}) {
  const [busy, setBusy] = useState(false)
  const run = async (action: 'cancel' | 'download') => {
    setBusy(true)
    try {
      if (action === 'cancel') await controller.cancel(job.job_id)
      else await controller.download(job.job_id)
    } catch (error) {
      void notify.error(error instanceof Error ? error.message : `Could not ${action} the export`)
    } finally {
      setBusy(false)
    }
  }
  if (job.state === 'ready') {
    return (
      <button
        className={compact ? 'btn btn-link note-export-history__action' : 'btn btn-primary'}
        type="button"
        disabled={busy}
        onClick={() => void run('download')}
      >
        {busy ? 'Opening…' : compact ? 'Download' : 'Download ZIP'}
      </button>
    )
  }
  if (ACTIVE_STATES.has(job.state)) {
    return (
      <button
        className={compact ? 'btn btn-link note-export-history__action' : 'btn'}
        type="button"
        disabled={busy}
        onClick={() => void run('cancel')}
      >
        {busy ? 'Canceling…' : 'Cancel'}
      </button>
    )
  }
  return null
}

function ExportJobCard({ controller, job }: { controller: NoteExportController; job: NoteExportJob }) {
  const percent = jobPercent(job)
  return (
    <article className="card card-border note-export-job note-export-job--latest" aria-label={`Latest ${job.format} export: ${stateLabel(job)}`}>
      <div className="card-body">
        <header>
          <div>
            <h5 className="card-title">{job.note_count} note{job.note_count === 1 ? '' : 's'} · {job.format === 'markdown' ? 'Markdown' : 'JSON'}</h5>
            <p>{job.include_images ? `${job.total_images} image${job.total_images === 1 ? '' : 's'}` : 'No images'} · {job.include_tags ? 'Tags included' : 'No tags'}</p>
          </div>
          <span className="badge">{stateLabel(job)}</span>
        </header>
        {ACTIVE_STATES.has(job.state) ? <progress className="progress progress-primary" value={percent} max="100" aria-label={`Export ${percent}% complete`} /> : null}
        <p className="note-export-job__retention">
          Requested <time dateTime={job.created_at}>{requestedAtLabel(job)}</time> · {expiryLabel(job)}{job.archive_bytes ? ` · ${bytes(job.archive_bytes)}` : ''}
        </p>
        {job.error_code ? <p className="note-export-job__error" role="status">{job.error_code.replaceAll('-', ' ')}</p> : null}
        <div className="card-actions"><ExportJobAction controller={controller} job={job} /></div>
      </div>
    </article>
  )
}

function ExportHistoryRow({ controller, job }: { controller: NoteExportController; job: NoteExportJob }) {
  return (
    <li className="list-row note-export-history__row" aria-label={`${job.format} export requested ${requestedAtLabel(job)}: ${stateLabel(job)}`}>
      <div className="note-export-history__summary">
        <time dateTime={job.created_at}>{requestedAtLabel(job)}</time>
        <span aria-hidden="true">·</span>
        <span>{job.note_count} note{job.note_count === 1 ? '' : 's'} · {job.format === 'markdown' ? 'Markdown' : 'JSON'}</span>
        <span className="badge">{stateLabel(job)}</span>
        {job.archive_bytes ? <span className="note-export-history__size">{bytes(job.archive_bytes)}</span> : null}
        {job.error_code ? <span className="note-export-history__error">{job.error_code.replaceAll('-', ' ')}</span> : null}
      </div>
      <ExportJobAction compact controller={controller} job={job} />
    </li>
  )
}

export function NoteExportsPanel({ controller }: { controller: NoteExportController }) {
  const jobs = [...controller.jobs].sort((left, right) => Date.parse(right.created_at) - Date.parse(left.created_at))
  const [latestJob, ...earlierJobs] = jobs
  return (
    <section className="settings-data-group note-exports-panel" aria-labelledby="recent-note-exports-title">
      <header className="settings-data-group__heading">
        <h4 id="recent-note-exports-title">Prepared exports</h4>
        <p>ZIP downloads are available for 30 days.</p>
      </header>
      {controller.error ? <p className="settings-data-inline-warning" role="status">Exports could not be refreshed. <button className="btn btn-ghost" type="button" onClick={() => void controller.refresh()}>Retry</button></p> : null}
      {latestJob
        ? <div className="note-export-jobs">
          <ExportJobCard controller={controller} job={latestJob} />
          {earlierJobs.length > 0 ? (
            <details className="collapse collapse-arrow note-export-history">
              <summary className="collapse-title">{earlierJobs.length} earlier export{earlierJobs.length === 1 ? '' : 's'}</summary>
              <div className="collapse-content">
                <ul className="list note-export-history__list">
                  {earlierJobs.map((job) => <ExportHistoryRow key={job.job_id} controller={controller} job={job} />)}
                </ul>
              </div>
            </details>
          ) : null}
        </div>
        : <p className="note-export-empty">{controller.attentionJobId ? 'Your export is being prepared. You can keep using miniMany.' : 'Selected-note exports will appear here.'}</p>}
    </section>
  )
}

function activityCopy(job: NoteExportJob | null): string {
  if (!job || ACTIVE_STATES.has(job.state)) return 'Export request is in progress…'
  if (job.state === 'ready') return 'Your export is ready to download'
  if (job.state === 'failed') return 'Export needs attention'
  if (job.state === 'canceled') return 'Export was canceled'
  return 'Export has expired'
}

function ActivityMark({ job, pending }: { job: NoteExportJob | null; pending: boolean }) {
  if (pending || !job || ACTIVE_STATES.has(job.state)) {
    return <ProcessingIndicator />
  }
  return <Icon name={job.state === 'ready' ? 'check' : job.state === 'failed' ? 'upload-error' : 'document'} />
}

export function NoteExportBrowseStatus({
  controller,
  requestError,
  requestPending,
  settingsHint,
  onDismiss,
  onOpen,
}: {
  controller: NoteExportController
  requestError: string | null
  requestPending: boolean
  settingsHint: boolean
  onDismiss(): void
  onOpen(): void
}) {
  if (settingsHint) {
    return (
      <aside className="alert alert-soft note-export-activity note-export-activity--hint" role="status" aria-live="polite">
        <Icon name="settings" />
        <span>Find export status in Settings.</span>
        <button className="btn btn-ghost" type="button" onClick={onOpen}>Open Settings</button>
      </aside>
    )
  }
  if (!requestPending && !requestError && !controller.attentionJobId) return null
  const job = controller.attentionJob
  const failed = Boolean(requestError) || job?.state === 'failed'
  return (
    <aside className={`alert alert-soft note-export-activity${failed ? ' alert-error note-export-activity--error' : ''}`} role="status" aria-live="polite">
      {requestError
        ? <Icon name="upload-error" />
        : <ActivityMark job={job} pending={requestPending} />}
      <span>{requestError ? 'Export request failed' : activityCopy(job)}</span>
      <span className="note-export-activity__actions">
        {controller.attentionJobId ? <button className="btn btn-ghost" type="button" onClick={onOpen}>View in Settings</button> : null}
        <button className="btn btn-ghost btn-square" type="button" onClick={onDismiss} aria-label="Dismiss export status"><Icon name="close" /></button>
      </span>
    </aside>
  )
}

export function NoteExportSettingsSummary({ controller }: { controller: NoteExportController }) {
  if (!controller.attentionJobId) return null
  const job = controller.attentionJob
  const failed = Boolean(controller.error) || job?.state === 'failed'
  return (
    <span className="settings-data-task__status" data-error={failed || undefined} role="status" aria-live="polite" aria-label="Latest export status">
      <span aria-hidden="true">
        {controller.error
          ? <Icon name="upload-error" />
          : <ActivityMark job={job} pending={!job} />}
      </span>
      <span>{failed ? 'Needs attention' : job ? stateLabel(job) : 'Preparing export'}</span>
    </span>
  )
}
